import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  ArrowBack as ArrowBackIcon,
  HelpOutline as HelpOutlineIcon,
  LightbulbOutlined as LightbulbIcon,
  People as PeopleIcon,
  PlayCircleOutline as PlayCircleIcon,
  ReceiptLong as ReceiptLongIcon,
  Sell as SellIcon,
  Storage as StorageIcon,
  WarningAmberOutlined as WarningIcon
} from '@mui/icons-material';

import PartnerLayout from '../../../components/partner/PartnerLayout';
import {
  Block,
  Item,
  PARTNER_HELP_SECTIONS,
  PartnerSection,
  PartnerSectionIcon,
  PartnerSectionId,
  resolveLocale
} from './partnerHelpContent';

/**
 * GUÍA GLOBAL del portal partner (/partner/ayuda).
 *
 * Gemela de la guía del cliente (/customer/help) y por el mismo motivo: el
 * portal partner mueve dinero real —bolsa, techo, márgenes, planes
 * recurrentes— y nada de eso se deduce mirando la pantalla. Un partner que no
 * entiende la diferencia entre su bolsa y su techo, o entre un plan y una
 * recarga, toma decisiones caras.
 *
 * Índice pegajoso + secciones ancladas: se lee entera la primera vez y después
 * se entra por enlace directo (#bolsa, #creditos…), que es lo que hace el
 * «saber más» de la ayuda de cada pantalla (PartnerHelpPanel).
 *
 * El contenido editorial NO vive aquí sino en partnerHelpContent.ts (bilingüe
 * por bloque): esta página solo sabe MAQUETAR bloques. Añadir una sección es
 * tocar el contenido, nunca este fichero.
 */

/** Alto de la barra pegajosa del portal partner: los anclajes tienen que
 *  descontarlo o el título queda oculto bajo el navbar. */
const NAV_OFFSET = 88;

const SECTION_ICONS: Record<PartnerSectionIcon, React.ReactElement> = {
  start: <PlayCircleIcon fontSize="small" />,
  wallet: <WalletIcon fontSize="small" />,
  clients: <PeopleIcon fontSize="small" />,
  credits: <WalletIcon fontSize="small" />,
  catalog: <SellIcon fontSize="small" />,
  storage: <StorageIcon fontSize="small" />,
  billing: <ReceiptLongIcon fontSize="small" />,
  faq: <HelpOutlineIcon fontSize="small" />
};

/** Lista «rótulo → explicación». Igual para `list` y numerada para `steps`: la
 *  única diferencia es la viñeta, no la estructura. */
const ItemList: React.FC<{ items: Item[]; numbered?: boolean }> = ({ items, numbered }) => {
  const theme = useTheme();
  return (
    <Box component="ul" sx={{ listStyle: 'none', m: 0, mb: 2.5, p: 0 }}>
      {items.map((item, idx) => (
        <Box
          component="li"
          key={item.k}
          sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', py: 0.9 }}
        >
          <Box
            sx={{
              flex: '0 0 auto',
              mt: 0.35,
              width: 22,
              height: 22,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 700,
              bgcolor: numbered ? 'primary.main' : alpha(theme.palette.primary.main, 0.12),
              color: numbered ? 'primary.contrastText' : 'primary.main'
            }}
          >
            {numbered ? idx + 1 : '•'}
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.65 }}>
            <Typography component="span" variant="body1" fontWeight={700} color="text.primary">
              {item.k}
            </Typography>
            {' — '}
            {item.v}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const BlockView: React.FC<{ block: Block; loc: 'es' | 'en' }> = ({ block, loc }) => {
  switch (block.kind) {
    case 'h':
      return (
        <Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1.25 }}>
          {block[loc]}
        </Typography>
      );
    case 'p':
      return (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.75 }}>
          {block[loc]}
        </Typography>
      );
    case 'tip':
      return (
        <Alert severity="info" icon={<LightbulbIcon fontSize="inherit" />} sx={{ mb: 2.5 }}>
          {block[loc]}
        </Alert>
      );
    case 'warn':
      return (
        <Alert severity="warning" icon={<WarningIcon fontSize="inherit" />} sx={{ mb: 2.5 }}>
          {block[loc]}
        </Alert>
      );
    case 'list':
      return <ItemList items={block[loc]} />;
    case 'steps':
      return <ItemList items={block[loc]} numbered />;
    default:
      return null;
  }
};

const SectionView: React.FC<{ section: PartnerSection; loc: 'es' | 'en' }> = ({ section, loc }) => {
  const theme = useTheme();
  const copy = section[loc];
  return (
    <Box component="section" id={section.id} sx={{ scrollMarginTop: `${NAV_OFFSET}px`, mb: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main'
          }}
        >
          {SECTION_ICONS[section.icon]}
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
          {copy.title}
        </Typography>
      </Box>
      <Typography
        variant="body1"
        sx={{ mb: 2.5, lineHeight: 1.7, color: 'text.primary', fontWeight: 500 }}
      >
        {copy.lead}
      </Typography>
      {section.blocks.map((block, idx) => (
        <BlockView key={`${section.id}-${idx}`} block={block} loc={loc} />
      ))}
      <Divider sx={{ mt: 4 }} />
    </Box>
  );
};

const PartnerHelpPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const loc = resolveLocale(i18n.language);
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState<PartnerSectionId>(PARTNER_HELP_SECTIONS[0].id);

  const scrollTo = useCallback((id: PartnerSectionId) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
    window.scrollTo({ top, behavior: 'smooth' });
  }, []);

  // Entrada por enlace directo (#bolsa, #creditos…): la ayuda de cada pantalla
  // enlaza así, y el navegador no sabe descontar el navbar pegajoso — de ahí el
  // scroll a mano. El timeout deja pintar antes.
  useEffect(() => {
    const hash = location.hash.replace('#', '') as PartnerSectionId;
    if (!hash) {
      window.scrollTo({ top: 0 });
      return;
    }
    setActive(hash);
    const timer = window.setTimeout(() => scrollTo(hash), 60);
    return () => window.clearTimeout(timer);
  }, [location.hash, scrollTo]);

  // Sección activa del índice: gana la primera que cruza la franja alta del
  // viewport, que es la que el lector tiene delante de los ojos.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActive(visible[0].target.id as PartnerSectionId);
      },
      { rootMargin: `-${NAV_OFFSET}px 0px -55% 0px`, threshold: 0 }
    );
    PARTNER_HELP_SECTIONS.forEach((section) => {
      const el = document.getElementById(section.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <PartnerLayout>
      <Box sx={{ bgcolor: 'background.paper', pt: 6, pb: 4, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Typography variant="overline" color="primary" sx={{ fontWeight: 800, letterSpacing: '0.14em' }}>
            {t('partner.help.eyebrow', 'Guía del portal de partners')}
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.03em',
              lineHeight: 1.12,
              mt: 1,
              mb: 1.5,
              fontSize: { xs: '1.9rem', sm: '2.3rem', md: '2.6rem' }
            }}
          >
            {t('partner.help.heroTitle', 'Tu negocio, explicado de principio a fin.')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 760, fontSize: '1.05rem', lineHeight: 1.7 }}>
            {t(
              'partner.help.heroLead',
              'Qué compras, qué vendes, dónde está tu margen y qué decides tú. Sin tecnicismos y sin letra pequeña: la diferencia entre tu bolsa y tu techo, entre un plan y una recarga, y entre un crédito y un importe.'
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/partner')}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {t('partner.help.backToDashboard', 'Volver al panel')}
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
              {t('partner.help.readingTime', 'Lectura completa ≈ 8 min')}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Grid container spacing={4}>
          {/* Índice lateral pegajoso (en móvil va arriba, sin pegar) */}
          <Grid item xs={12} md={3.5}>
            <Paper
              variant="outlined"
              sx={{ borderRadius: 3, p: 1, position: { md: 'sticky' }, top: { md: `${NAV_OFFSET}px` } }}
            >
              <Typography
                variant="overline"
                sx={{ fontWeight: 800, letterSpacing: 1.1, px: 1.5, color: 'text.secondary' }}
              >
                {t('partner.help.contents', 'En esta guía')}
              </Typography>
              <List dense disablePadding>
                {PARTNER_HELP_SECTIONS.map((section) => (
                  <ListItemButton
                    key={section.id}
                    selected={active === section.id}
                    onClick={() => navigate(`/partner/ayuda#${section.id}`)}
                    sx={{ borderRadius: 2, '&.Mui-selected': { bgcolor: 'action.selected' } }}
                  >
                    <ListItemIcon
                      sx={{ minWidth: 34, color: active === section.id ? 'primary.main' : 'text.secondary' }}
                    >
                      {SECTION_ICONS[section.icon]}
                    </ListItemIcon>
                    <ListItemText
                      primary={section[loc].title}
                      primaryTypographyProps={{
                        fontSize: '0.9rem',
                        fontWeight: active === section.id ? 700 : 500
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8.5}>
            {PARTNER_HELP_SECTIONS.map((section) => (
              <SectionView key={section.id} section={section} loc={loc} />
            ))}
            <Alert severity="info" sx={{ mt: 2 }}>
              {t(
                'partner.help.footer',
                '¿Te falta algo aquí? Escríbenos: esta guía se amplía con lo que preguntáis.'
              )}
            </Alert>
          </Grid>
        </Grid>
      </Container>
    </PartnerLayout>
  );
};

export default PartnerHelpPage;
